import { getIn } from "@utils/index"
import React from "react"

type Args = {
  customerCountryCode?: string
  customerLocationFetched?: boolean
  formattedProducts: any[]
  prohibitedProductRules?: any[]
}

const AVAILABILITY_US = "Sell in US"
const AVAILABILITY_INTL = "Sell INTL"
const AVAILABILITY_EVERYWHERE = "Sell Everywhere"

/**
 * Manages product availability logic including:
 * - Country-prohibited products
 * - Custom print availability by region
 */
export function useProductAvailability({
  customerCountryCode,
  customerLocationFetched,
  formattedProducts,
  prohibitedProductRules = [],
}: Args) {
  // Country-prohibited products
  const [notAvailableInCustomerCountry, setNotAvailableInCustomerCountry] =
    React.useState<Array<string>>([])

  React.useEffect(() => {
    if (
      customerLocationFetched &&
      customerCountryCode &&
      prohibitedProductRules.length > 0
    ) {
      const prohibitedProducts: Array<string> = []
      prohibitedProductRules.forEach((rule) => {
        const countries = getIn(rule, "countries", [])
        const products = getIn(rule, "products", [])

        if (countries.includes(customerCountryCode)) {
          products.forEach((product) => {
            prohibitedProducts.push(product.slug)
          })
        }
      })

      setNotAvailableInCustomerCountry(prohibitedProducts)
    }
  }, [
    prohibitedProductRules.length,
    customerLocationFetched,
    customerCountryCode,
  ])

  // Custom print availability
  const [availablePrintedDesignVariants, setAvailablePrintedDesignVariants] =
    React.useState<any>([])
  const [
    unavailablePrintedDesignVariants,
    setUnavailablePrintedDesignVariants,
  ] = React.useState<any>([])

  React.useEffect(() => {
    if (customerLocationFetched && customerCountryCode) {
      const availableSkus: any = []
      const unavailableSkus: any = []

      formattedProducts.forEach((product) => {
        if (product.customPrint) {
          product.variants.forEach((variant) => {
            if (customerCountryCode === "US") {
              if (
                variant.availability === AVAILABILITY_US ||
                variant.availability === AVAILABILITY_EVERYWHERE
              ) {
                availableSkus.push(variant.sku)
              } else {
                unavailableSkus.push(variant.sku)
              }
            } else {
              if (
                variant.availability === AVAILABILITY_INTL ||
                variant.availability === AVAILABILITY_EVERYWHERE
              ) {
                availableSkus.push(variant.sku)
              } else {
                unavailableSkus.push(variant.sku)
              }
            }
          })
        }
      })

      setAvailablePrintedDesignVariants(availableSkus)
      setUnavailablePrintedDesignVariants(unavailableSkus)
    }
  }, [formattedProducts.length, customerLocationFetched, customerCountryCode])

  return {
    availablePrintedDesignVariants,
    notAvailableInCustomerCountry,
    unavailablePrintedDesignVariants,
  }
}
